import { defineStore } from 'pinia';
import axiosInstance from '@/services/axiosInstance';
import { useSocketService } from '@/services/socketService';
import { useUserStore } from '@/stores/user/userStore';
import { useMinimizedChatsStore } from '@/stores/chat/minimizedChatsStore';
import type { Socket } from 'socket.io-client';

// ──────────────────────────────────────────────
//  Types
// ──────────────────────────────────────────────

export interface IChatUser {
  _id: string;
  firstName: string;
  lastName: string;
  name?: string;
  nickName?: string;
  avatar?: string;
}

export interface IConversationMember {
  user: IChatUser;
  joinedAt: string;
  lastReadAt?: string;
  mutedUntil?: string | null;
  nickname?: string;
  role: 'admin' | 'member';
  acceptedAt?: string | null;
}

export interface IConversation {
  _id: string;
  type: 'direct' | 'group' | 'team';
  name?: string;
  avatar?: string;
  _team?: string;
  members: IConversationMember[];
  lastMessage?: {
    content?: string;
    sender?: IChatUser | string;
    type?: string;
    createdAt?: string;
  };
  settings?: {
    adminOnly?: boolean;
    allowMedia?: boolean;
  };
  unreadCount?: number;
  /** Direct conv only — čí konv. byla ještě nepřijata aktuálním uživatelem */
  pending?: boolean;
  /** Direct conv only — ID druhého člena pokud je ještě pending */
  pendingFromUserId?: string | null;
  /** Direct conv only — vztah bloká mezi mnou a druhou stranou */
  blockStatus?: { iBlocked: boolean; theyBlockedMe: boolean };
  createdAt: string;
  updatedAt: string;
}

export interface IAttachment {
  originalName: string;
  fileName: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  duration?: number;
  width?: number;
  height?: number;
}

export interface IReaction {
  emoji: string;
  user: string | IChatUser;
  createdAt: string;
}

export interface IReadReceipt {
  user: string | IChatUser;
  readAt: string;
}

export interface IPollVote {
  user: string | IChatUser;
  votedAt: string;
}

export interface IPollOption {
  _id: string;
  text: string;
  votes: IPollVote[];
}

export interface IPoll {
  question: string;
  options: IPollOption[];
  multipleChoice: boolean;
  createdBy: string | IChatUser;
  closedAt?: string | null;
}

export interface IChatMessage {
  _id: string;
  conversation: string;
  sender: IChatUser;
  content?: string | null;
  type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'system' | 'poll';
  attachments: IAttachment[];
  poll?: IPoll | null;
  replyTo?: IChatMessage | null;
  reactions: IReaction[];
  readBy: IReadReceipt[];
  deletedAt?: string | null;
  deletedBy?: string | null;
  editedAt?: string | null;
  systemKey?: string | null;
  systemParams?: Record<string, any> | null;
  _deleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TypingUser {
  userId: string;
  userName: string;
  timeout?: ReturnType<typeof setTimeout>;
}

// ──────────────────────────────────────────────
//  Store
// ──────────────────────────────────────────────

export const useChatStore = defineStore('chatStore', {
  state: () => ({
    conversations: [] as IConversation[],
    activeConversation: null as IConversation | null,
    messages: [] as IChatMessage[],
    totalUnreadCount: 0,
    /** ID of the first live unread message in the active conversation (set when receiving messages while viewing) */
    liveUnreadFromId: null as string | null,
    loading: false,
    messagesLoading: false,
    hasMoreMessages: true,
    currentPage: 1,
    typingUsers: {} as Record<string, TypingUser[]>, // conversationId → users
    socketListenersAttached: false,
  }),

  getters: {
    sortedConversations: (state) => {
      return [...state.conversations].sort((a, b) => {
        const aTime = a.lastMessage?.createdAt || a.updatedAt;
        const bTime = b.lastMessage?.createdAt || b.updatedAt;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
    },

    currentTypingUsers: (state) => {
      if (!state.activeConversation) return [];
      return state.typingUsers[state.activeConversation._id] || [];
    },

    getConversationById: (state) => (id: string) => {
      return state.conversations.find(c => c._id === id);
    },
  },

  actions: {
    // ── Socket management ───────────────────────

    initSocketListeners() {
      if (this.socketListenersAttached) return;

      const socket = useSocketService();
      const userStore = useUserStore();

      // Join user's personal room for notifications
      const userId = userStore.getUserId;
      if (userId) {
        if (socket.connected) {
          socket.emit('joinUserRoom', userId);
        }
        // (Re)join after every connect/reconnect
        socket.on('connect', () => {
          socket.emit('joinUserRoom', userId);
          // Re-join active conversation room after reconnect
          if (this.activeConversation?._id) {
            socket.emit('chat:join', this.activeConversation._id);
          }
        });
      }

      // New message in any conversation
      socket.on('chat:notification', (data: { conversationId: string; message: IChatMessage }) => {
        this.handleIncomingNotification(data);
      });

      // New message in active conversation
      socket.on('chat:newMessage', (data: { conversationId: string; message: IChatMessage }) => {
        this.handleIncomingMessage(data);
      });

      // Messages read notification
      socket.on('chat:messagesRead', (data: { conversationId: string; userId: string; readAt: string }) => {
        this.handleMessagesRead(data);
      });

      // Reaction update
      socket.on('chat:reactionUpdate', (data: { conversationId: string; messageId: string; reactions: IReaction[] }) => {
        const msg = this.messages.find(m => m._id === data.messageId);
        if (msg) msg.reactions = data.reactions;
      });

      // Message deleted
      socket.on('chat:messageDeleted', (data: { conversationId: string; messageId: string }) => {
        const msg = this.messages.find(m => m._id === data.messageId);
        if (msg) {
          msg._deleted = true;
          msg.content = null;
          msg.attachments = [];
          msg.type = 'system';
        }
      });

      // Poll update
      socket.on('chat:pollUpdate', (data: { conversationId: string; message: IChatMessage }) => {
        const idx = this.messages.findIndex(m => m._id === data.message._id);
        if (idx >= 0) this.messages[idx] = data.message;
      });

      // Message edited
      socket.on('chat:messageEdited', (data: { conversationId: string; message: IChatMessage }) => {
        const idx = this.messages.findIndex(m => m._id === data.message._id);
        if (idx >= 0) this.messages[idx] = data.message;
      });

      // Typing indicators
      socket.on('chat:typing', (data: { conversationId: string; userId: string; userName: string }) => {
        if (data.userId === userStore.getUserId) return;
        if (!this.typingUsers[data.conversationId]) this.typingUsers[data.conversationId] = [];
        const arr = this.typingUsers[data.conversationId];
        const existing = arr.find(u => u.userId === data.userId);
        if (existing) {
          clearTimeout(existing.timeout);
          existing.timeout = setTimeout(() => {
            this.removeTypingUser(data.conversationId, data.userId);
          }, 4000);
        } else {
          arr.push({
            userId: data.userId,
            userName: data.userName,
            timeout: setTimeout(() => {
              this.removeTypingUser(data.conversationId, data.userId);
            }, 4000),
          });
        }
      });

      socket.on('chat:stopTyping', (data: { conversationId: string; userId: string }) => {
        this.removeTypingUser(data.conversationId, data.userId);
      });

      this.socketListenersAttached = true;
    },

    removeTypingUser(conversationId: string, userId: string) {
      const arr = this.typingUsers[conversationId];
      if (!arr) return;
      const idx = arr.findIndex(u => u.userId === userId);
      if (idx >= 0) {
        clearTimeout(arr[idx].timeout);
        arr.splice(idx, 1);
      }
    },

    joinConversationRoom(conversationId: string) {
      const socket = useSocketService();
      socket.emit('chat:join', conversationId);
    },

    leaveConversationRoom(conversationId: string) {
      const socket = useSocketService();
      socket.emit('chat:leave', conversationId);
    },

    emitTyping(conversationId: string) {
      const socket = useSocketService();
      const userStore = useUserStore();
      const user = userStore.user;
      socket.emit('chat:typing', {
        conversationId,
        userId: userStore.getUserId,
        userName: user ? `${user.firstName} ${user.lastName}` : '',
      });
    },

    emitStopTyping(conversationId: string) {
      const socket = useSocketService();
      const userStore = useUserStore();
      socket.emit('chat:stopTyping', {
        conversationId,
        userId: userStore.getUserId,
      });
    },

    // ── API calls ───────────────────────────────

    async fetchConversations(page = 1) {
      this.loading = true;
      try {
        const { data } = await axiosInstance.get('/chat/conversations', { params: { page, limit: 30 } });
        this.conversations = data.conversations;
        return data;
      } catch (err) {
        console.error('Error fetching conversations:', err);
      } finally {
        this.loading = false;
      }
    },

    async fetchConversation(id: string) {
      try {
        const { data } = await axiosInstance.get(`/chat/conversations/${id}`);
        this.activeConversation = data;

        // Update in conversations list too
        const idx = this.conversations.findIndex(c => c._id === id);
        if (idx >= 0) this.conversations[idx] = { ...this.conversations[idx], ...data };
        else this.conversations.unshift(data);

        return data;
      } catch (err) {
        console.error('Error fetching conversation:', err);
      }
    },

    async createDirectConversation(recipientId: string) {
      try {
        const { data } = await axiosInstance.post('/chat/conversations/direct', { recipientId });
        // Add to list if not present
        const idx = this.conversations.findIndex(c => c._id === data._id);
        if (idx < 0) this.conversations.unshift(data);
        return data;
      } catch (err) {
        console.error('Error creating direct conversation:', err);
        throw err;
      }
    },

    async createGroupConversation(memberIds: string[], name?: string) {
      try {
        const { data } = await axiosInstance.post('/chat/conversations/group', { memberIds, name });
        this.conversations.unshift(data);
        return data;
      } catch (err) {
        console.error('Error creating group conversation:', err);
        throw err;
      }
    },

    async ensureTeamConversation(teamId: string) {
      try {
        const { data } = await axiosInstance.post(`/chat/conversations/team/${teamId}`);
        const idx = this.conversations.findIndex(c => c._id === data._id);
        if (idx < 0) this.conversations.unshift(data);
        return data;
      } catch (err) {
        console.error('Error ensuring team conversation:', err);
        throw err;
      }
    },

    async fetchMessages(conversationId: string, page = 1) {
      this.messagesLoading = true;
      try {
        const { data } = await axiosInstance.get(`/chat/conversations/${conversationId}/messages`, {
          params: { page, limit: 20 },
        });
        if (page === 1) {
          this.messages = data.messages;
        } else {
          // Prepend older messages
          this.messages = [...data.messages, ...this.messages];
        }
        this.hasMoreMessages = data.hasMore;
        this.currentPage = page;
        return data;
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        this.messagesLoading = false;
      }
    },

    async sendMessage(conversationId: string, payload: { content?: string; files?: File[]; replyTo?: string }) {
      try {
        const formData = new FormData();
        if (payload.content) formData.append('content', payload.content);
        if (payload.replyTo) formData.append('replyTo', payload.replyTo);
        if (payload.files) {
          for (const file of payload.files) {
            formData.append('files', file);
          }
        }

        const { data } = await axiosInstance.post(`/chat/conversations/${conversationId}/messages`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        // Add to local messages immediately so it appears without waiting for socket
        if (data && data._id) {
          const exists = this.messages.find(m => m._id === data._id);
          if (!exists) {
            this.messages.push(data);
          }
        }

        // Update lastMessage on conversation in list
        const conv = this.conversations.find(c => c._id === conversationId);
        if (conv && data) {
          conv.lastMessage = {
            content: data.content || '📎',
            sender: data.sender,
            type: data.type,
            createdAt: data.createdAt,
          };
        }

        return data;
      } catch (err) {
        console.error('Error sending message:', err);
        throw err;
      }
    },

    async editMessage(messageId: string, content: string) {
      try {
        const { data } = await axiosInstance.put(`/chat/messages/${messageId}`, { content });
        const idx = this.messages.findIndex(m => m._id === messageId);
        if (idx >= 0) this.messages[idx] = data;
        return data;
      } catch (err) {
        console.error('Error editing message:', err);
        throw err;
      }
    },

    async deleteMessage(messageId: string) {
      try {
        await axiosInstance.delete(`/chat/messages/${messageId}`);
        const msg = this.messages.find(m => m._id === messageId);
        if (msg) {
          msg._deleted = true;
          msg.content = null;
          msg.attachments = [];
        }
      } catch (err) {
        console.error('Error deleting message:', err);
        throw err;
      }
    },

    async toggleReaction(messageId: string, emoji: string) {
      try {
        const { data } = await axiosInstance.post(`/chat/messages/${messageId}/reactions`, { emoji });
        const msg = this.messages.find(m => m._id === messageId);
        if (msg) msg.reactions = data.reactions;
      } catch (err) {
        console.error('Error toggling reaction:', err);
      }
    },

    async createPoll(conversationId: string, payload: { question: string; options: string[]; multipleChoice?: boolean }) {
      try {
        const { data } = await axiosInstance.post(`/chat/conversations/${conversationId}/poll`, payload);
        if (data && data._id) {
          const exists = this.messages.find(m => m._id === data._id);
          if (!exists) this.messages.push(data);
        }
        const conv = this.conversations.find(c => c._id === conversationId);
        if (conv && data) {
          conv.lastMessage = {
            content: `📊 ${payload.question}`,
            sender: data.sender,
            type: 'poll',
            createdAt: data.createdAt,
          };
        }
        return data;
      } catch (err) {
        console.error('Error creating poll:', err);
        throw err;
      }
    },

    async votePoll(messageId: string, optionIndex: number) {
      try {
        const { data } = await axiosInstance.post(`/chat/messages/${messageId}/vote`, { optionIndex });
        const idx = this.messages.findIndex(m => m._id === messageId);
        if (idx >= 0) this.messages[idx] = data;
        return data;
      } catch (err) {
        console.error('Error voting on poll:', err);
        throw err;
      }
    },

    async editPoll(messageId: string, payload: { question: string; options: string[]; multipleChoice?: boolean }) {
      try {
        const { data } = await axiosInstance.put(`/chat/messages/${messageId}/poll`, payload);
        const idx = this.messages.findIndex(m => m._id === messageId);
        if (idx >= 0) this.messages[idx] = data;
        return data;
      } catch (err) {
        console.error('Error editing poll:', err);
        throw err;
      }
    },

    async markAsRead(conversationId: string) {
      try {
        await axiosInstance.post(`/chat/conversations/${conversationId}/read`);
        // Update local state
        const conv = this.conversations.find(c => c._id === conversationId);
        if (conv && conv.unreadCount) {
          this.totalUnreadCount = Math.max(0, this.totalUnreadCount - conv.unreadCount);
          conv.unreadCount = 0;
        }
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    },

    async fetchUnreadCount() {
      try {
        const { data } = await axiosInstance.get('/chat/unread-count');
        this.totalUnreadCount = data.unreadCount;
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    },

    async searchMessages(conversationId: string, query: string, page = 1) {
      try {
        const { data } = await axiosInstance.get(`/chat/conversations/${conversationId}/search`, {
          params: { q: query, page, limit: 20 },
        });
        return data;
      } catch (err) {
        console.error('Error searching messages:', err);
        throw err;
      }
    },

    async getMedia(conversationId: string, type?: string, page = 1) {
      try {
        const { data } = await axiosInstance.get(`/chat/conversations/${conversationId}/media`, {
          params: { type, page, limit: 30 },
        });
        return data;
      } catch (err) {
        console.error('Error fetching media:', err);
        throw err;
      }
    },

    async updateConversation(conversationId: string, updates: { name?: string; avatar?: string; settings?: any }) {
      try {
        const { data } = await axiosInstance.put(`/chat/conversations/${conversationId}`, updates);
        const idx = this.conversations.findIndex(c => c._id === conversationId);
        if (idx >= 0) Object.assign(this.conversations[idx], data);
        if (this.activeConversation?._id === conversationId) Object.assign(this.activeConversation, data);
        return data;
      } catch (err) {
        console.error('Error updating conversation:', err);
        throw err;
      }
    },

    async addMembers(conversationId: string, memberIds: string[], role: 'member' | 'admin' = 'member') {
      try {
        const { data } = await axiosInstance.post(`/chat/conversations/${conversationId}/members`, { memberIds, role });
        return data;
      } catch (err) {
        console.error('Error adding members:', err);
        throw err;
      }
    },

    async acceptConversation(conversationId: string) {
      try {
        const { data } = await axiosInstance.post(`/chat/conversations/${conversationId}/accept`);
        if (this.activeConversation?._id === conversationId) {
          Object.assign(this.activeConversation, data);
        }
        const idx = this.conversations.findIndex(c => c._id === conversationId);
        if (idx >= 0) Object.assign(this.conversations[idx], data);
        return data;
      } catch (err) {
        console.error('Error accepting conversation:', err);
        throw err;
      }
    },

    async removeMember(conversationId: string, memberId: string) {
      try {
        const { data } = await axiosInstance.delete(`/chat/conversations/${conversationId}/members/${memberId}`);
        return data;
      } catch (err) {
        console.error('Error removing member:', err);
        throw err;
      }
    },

    async softDeleteConversation(conversationId: string) {
      try {
        const { data } = await axiosInstance.delete(`/chat/conversations/${conversationId}/soft`);
        this.conversations = this.conversations.filter(c => c._id !== conversationId);
        return data;
      } catch (err) {
        console.error('Error soft-deleting conversation:', err);
        throw err;
      }
    },

    // ── Incoming socket handlers ────────────────

    handleIncomingMessage(data: { conversationId: string; message: IChatMessage }) {
      // Only add if we're viewing this conversation
      if (this.activeConversation?._id === data.conversationId) {
        const exists = this.messages.find(m => m._id === data.message._id);
        if (!exists) {
          this.messages.push(data.message);
        }
      }
    },

    handleIncomingNotification(data: { conversationId: string; message: IChatMessage }) {
      const userStore = useUserStore();
      // Update conversation in list (lastMessage always updates for sidebar)
      const conv = this.conversations.find(c => c._id === data.conversationId);
      if (conv) {
        conv.lastMessage = {
          content: data.message.content || '📎',
          sender: data.message.sender,
          type: data.message.type,
          createdAt: data.message.createdAt,
        };

        // If user is viewing this conversation, don't increment badge
        // but set a live-unread marker so the UI can show a divider
        if (this.activeConversation?._id === data.conversationId) {
          const senderId = typeof data.message.sender === 'object'
            ? data.message.sender._id
            : data.message.sender;
          // Only mark as unread for messages from others
          if (senderId !== userStore.getUserId && !this.liveUnreadFromId) {
            this.liveUnreadFromId = data.message._id;
          }
          return;
        }

        conv.unreadCount = (conv.unreadCount || 0) + 1;
      }
      this.totalUnreadCount++;

      // Track unread for minimized chats
      const minimizedStore = useMinimizedChatsStore();
      if (minimizedStore.isMinimized(data.conversationId)) {
        minimizedStore.incrementUnread(data.conversationId);
      }
    },

    handleMessagesRead(data: { conversationId: string; userId: string; readAt: string }) {
      // Update readBy on visible messages
      if (this.activeConversation?._id === data.conversationId) {
        for (const msg of this.messages) {
          if (!msg.readBy.find(r => (typeof r.user === 'string' ? r.user : (r.user as any)?._id) === data.userId)) {
            msg.readBy.push({ user: data.userId, readAt: data.readAt });
          }
        }
      }
    },

    // ── Cleanup ─────────────────────────────────

    clearActiveConversation() {
      if (this.activeConversation) {
        this.leaveConversationRoom(this.activeConversation._id);
      }
      this.activeConversation = null;
      this.messages = [];
      this.liveUnreadFromId = null;
      this.hasMoreMessages = true;
      this.currentPage = 1;
    },
  },
});
